import type { Identifier } from "ra-core";

import { getDataProvider as getAtomicDataProvider } from "@/components/atomic-crm/providers/supabase";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";

import { escribirAjustes, leerAjustes } from "../dominio/ajustes";
import { condicionPersonas } from "../dominio/equipo";
import { ORDEN_LLAMADA, ordenParaLlamar } from "../dominio/llamadas";
import {
  esAnaliticaNoDisponible,
  type DatosAnalisis,
  type MesInforme,
} from "../dominio/analitica";
import type { AlertaPanel, DatosPanel } from "../dominio/panelInicio";
import {
  ErrorEtapa,
  ETAPAS_NUEVAS,
  validarCambioEtapa,
} from "../dominio/etapas";
import { normalizarEmail } from "../dominio/normalizacion";
import {
  aplicarResultado,
  datosParaRpc,
  type DatosResultado,
  type EfectoResultado,
} from "../dominio/resultado";
import type {
  AjustesNevora,
  Canal,
  Colaboracion,
  ConceptoCobro,
  ConsultorPosible,
  CorreoDeNegocio,
  Etapa,
  EstadoColaboracion,
  FechaHoraISO,
  FechaISO,
  EstadoAgenda,
  MetricasConsultor,
  MiComision,
  Negocio,
  NivelInteres,
  PersonaConsultoria,
  Resultado,
  RolComercial,
} from "../dominio/tipos";
import type { FilaResumen } from "../dominio/resumen";

export type ParamsRegistrarResultado = {
  negocio: Negocio;
  canal: Canal;
  /** null = anotar el contacto sin resultado (la tarjeta no se mueve). */
  resultado: Resultado | null;
  datos: DatosResultado;
  ajustes: Pick<AjustesNevora, "maxIntentos">;
};

export type ParamsRegistrarCobro = {
  proyectoId: Identifier;
  /** Base imponible sin IVA. */
  importe: number;
  fecha: FechaISO;
  importeTotal?: number | null;
  nota?: string | null;
  /** El mantenimiento nunca lleva comisión. */
  concepto?: ConceptoCobro;
};

/** Datos comerciales de una persona (pantalla Equipo; solo administradores). */
export type DatosComercial = {
  rol_comercial: RolComercial | null;
  recibe_leads: boolean;
  porcentaje_reparto: number | null;
  tope_pendientes: number | null;
  comision_pct: number | null;
  team_lead_id?: Identifier | null;
};

/** Respuesta de nevora_posibles_duplicados. */
export type PosiblesDuplicados = {
  n: number;
  fichas: { id: Identifier; nombre: string; dueno: string | null }[];
};

/** Una fila de la pantalla Equipo (nevora_metricas_equipo). */
export type MetricasSetter = {
  sale_id: Identifier;
  asignados: number;
  pendientes: number;
  recuperados: number;
  llamadas: number;
  /** 28c: «Interesado, falta confirmar fecha» registrados por la persona. */
  interesados?: number;
  citas: number;
  /** 36: «No se presentó» de las consultorías que agendó (nunca cuentan como hechas). */
  no_shows?: number;
  consultorias: number;
  propuestas: number;
  cierres: number;
  cobros: number;
  cobrado: number;
  comision: number;
};

/**
 * Datos que acompañan a un cambio de etapa a mano (fechas de consultoría, presentación, vuelta;
 * 39: el importe aceptado al pasar a «Aceptada»).
 */
export type ExtrasCambioEtapa = Partial<
  Pick<
    Negocio,
    | "orden"
    | "fecha_reunion"
    | "fecha_presentacion"
    | "fecha_vuelta"
    | "importe_total"
  >
>;

/** 36: por qué y desde cuándo una ficha está en la lista de no contactar. */
export type EntradaNoContactar = {
  motivo: string | null;
  fecha: FechaHoraISO;
  app_origen: string;
  canal: string;
};

/** Un móvil con las notificaciones activadas (33). */
export type MovilPush = {
  id: Identifier;
  endpoint: string;
  dispositivo: string | null;
  created_at: string;
  ultimo_envio_at: string | null;
  ultimo_error: string | null;
};

export type NevoraDataProvider = CrmDataProvider & {
  /** Botones de contacto y resultado de la presentación (RPC registrar_resultado). */
  registrarResultado(
    params: ParamsRegistrarResultado,
  ): Promise<{ negocio: Negocio; efecto: EfectoResultado }>;
  /** 28d: resumen de llamadas por persona en [desde, hasta) (RPC nevora_resumen_llamadas). */
  resumenLlamadas(desde: Date, hasta: Date): Promise<FilaResumen[]>;
  /** 28d: ¿la base ya tiene el resumen? (proyectos_vista trae `interes`). */
  resumenDisponible(): Promise<boolean>;
  /** 28d: nivel de interés de un proyecto, desde la tarjeta o la ficha. */
  cambiarInteres(
    proyectoId: Identifier,
    interes: NivelInteres | null,
  ): Promise<void>;
  /** Cambio de etapa a mano (arrastrar en el tablero, menú de la ficha). */
  moverProyecto(
    proyecto: Pick<Negocio, "proyecto_id" | "embudo" | "etapa" | "no_llamar">,
    destino: Etapa,
    extras?: ExtrasCambioEtapa,
  ): Promise<void>;
  /** Solo la posición dentro de la columna. */
  ordenarProyecto(proyectoId: Identifier, orden: number): Promise<void>;
  /** Cobro (el primero pasa el proyecto a Clientes). Solo administradores. */
  registrarCobro(params: ParamsRegistrarCobro): Promise<void>;
  /** Compra nueva de un cliente: proyecto nuevo en Ventas · Preparando propuesta. */
  nuevoProyecto(negocioId: Identifier, nombre?: string | null): Promise<void>;
  /** Vuelta automática de Largo plazo (RPC; también la hace pg_cron cada mañana). */
  revisarLargoPlazo(): Promise<number>;
  /** Rol, reparto y comisión de una persona (solo administradores). */
  configurarComercial(saleId: Identifier, datos: DatosComercial): Promise<void>;
  /** Asignar fichas a mano, una o varias (solo administradores). null = sin asignar. */
  asignar(ids: Identifier[], a: Identifier | null): Promise<number>;
  /** 45: las cifras del panel de Inicio (periodo con el último día incluido). */
  panel(
    desde: string,
    hasta: string,
    personas: number[] | null,
    origen?: string | null,
  ): Promise<DatosPanel>;
  /** 45: lo que hay que mirar ahora (sin periodo). */
  panelAlertas(personas: number[] | null): Promise<AlertaPanel[]>;
  /** 47: canal, sector, pérdidas, velocidad, franjas y previsión del periodo. */
  panelAnalisis(
    desde: string,
    hasta: string,
    personas: number[] | null,
    origen?: string | null,
  ): Promise<DatosAnalisis>;
  /** 47: el informe mes a mes (del más antiguo al actual). */
  informeMensual(
    meses: number,
    personas: number[] | null,
    origen?: string | null,
  ): Promise<MesInforme[]>;
  /**
   * 47: pasar a «No interesado» o «Largo plazo» a mano con su motivo (queda en la nota del
   * cambio). Sin la 47 en la base, se mueve sin motivo.
   */
  moverConMotivo(
    proyecto: Pick<Negocio, "proyecto_id" | "embudo" | "etapa" | "no_llamar">,
    destino: "no_interesado" | "largo_plazo",
    motivo: string,
    fechaVuelta?: string | null,
  ): Promise<void>;
  /** Consultorías agendadas o hechas en la semana de esa fecha (tope de 5). */
  consultoriasSemana(fecha: FechaISO): Promise<number>;
  /** Embudo por setter entre dos fechas (solo administradores). */
  metricasEquipo(
    desde: FechaHoraISO,
    hasta: FechaHoraISO,
  ): Promise<MetricasSetter[]>;
  /** ¿Tiene el negocio una campaña viva en Nevora Prospect? (aviso, S4 informativo). */
  inscripcionViva(negocioId: Identifier): Promise<boolean>;
  /** 32: «Sacar de la campaña de email» (Jan y Robert). Devuelve cuántas inscripciones cerró. */
  sacarDeCampana(negocioId: Identifier): Promise<number>;
  /** 32: ¿la base ya tiene el botón? Sin la 32 la función no existe (PGRST202). */
  sacarDeCampanaDisponible(): Promise<boolean>;
  /** 34 (19/10/2026): de las etapas nuevas (ETAPAS_NUEVAS), cuáles tiene ya la base. */
  etapasNuevasEnLaBase(): Promise<Etapa[]>;
  /** Marca avisos como leídos. */
  marcarAvisosLeidos(ids: Identifier[]): Promise<void>;
  /**
   * Capturas de la web de un lead de Prospect: enlaces firmados de 10 minutos que da la
   * función `capturas` solo si el usuario puede ver esa ficha (contrato §10.5).
   */
  capturasProspect(
    negocioId: Identifier,
  ): Promise<{ tipo: string; url: string | null }[]>;
  /**
   * Posible duplicado por nombre parecido (migración 28, 14/10): no bloquea. Administradores:
   * las fichas con su dueño; setter: solo cuántas. null mientras la base no tenga la función.
   */
  posiblesDuplicados(nombre: string): Promise<PosiblesDuplicados | null>;
  getAjustes(): Promise<AjustesNevora>;
  updateAjustes(ajustes: AjustesNevora): Promise<AjustesNevora>;
  /** Último acceso de cada usuario (user_id → fecha ISO o null). Solo administradores. */
  ultimosAccesos(): Promise<Record<string, string | null>>;
  /** 33: ¿la base ya tiene las notificaciones push? (solo lo pregunta Perfil y Ajustes). */
  pushDisponible(): Promise<boolean>;
  /** 33: mis móviles con las notificaciones activadas. */
  misMovilesPush(): Promise<MovilPush[]>;
  /** 33: da de alta (o pasa a mí) este móvil. */
  guardarMovilPush(movil: {
    endpoint: string;
    p256dh: string;
    auth: string;
    dispositivo: string;
  }): Promise<void>;
  /** 33: quita un móvil mío (por su dirección de push o por su id). */
  quitarMovilPush(movil: { endpoint?: string; id?: Identifier }): Promise<void>;
  /** 33: la clave pública VAPID (la da la función `push`). */
  clavePublicaPush(): Promise<string>;
  /** 33: «Mandarme una de prueba» a todos mis móviles. */
  pruebaPush(): Promise<{ enviadas: number; moviles: number }>;
  /**
   * 30: borra un usuario sin datos (función `users`, DELETE). Si tiene algo, lanza un error con
   * `code: "tiene_datos"` y `asociado` (qué tiene), para ofrecer desactivarlo.
   */
  borrarUsuario(saleId: Identifier): Promise<void>;
  /** 30: sube la foto (ya reducida) a su carpeta del bucket privado `avatares`; devuelve la ruta. */
  subirFotoUsuario(saleId: Identifier, foto: Blob): Promise<string>;
  /** 30: pone o quita (null) la foto de un usuario (función `users`). */
  cambiarFotoUsuario(saleId: Identifier, ruta: string | null): Promise<void>;
  /** 30: enlace firmado de una hora para ver una foto; null si no se puede. */
  urlFotoUsuario(ruta: string): Promise<string | null>;
  /** 30: borra un fichero de foto que ya no se usa (sin error si no está). */
  borrarFicheroFoto(ruta: string): Promise<void>;
  /** 36: quién puede hacer una consultoría (administradores y consultores activos). */
  consultoresPosibles(): Promise<ConsultorPosible[]>;
  /** 36: quién hace la consultoría de un proyecto, en orden (el primero se queda la ficha). */
  consultoresDe(proyectoId: Identifier): Promise<Identifier[]>;
  /** 36: pone quién hace la consultoría (el dueño o un administrador). */
  ponerConsultores(
    proyectoId: Identifier,
    personas: Identifier[],
  ): Promise<void>;
  /** 36: confirma la cita de la consultoría (o la desmarca); desde la 39, quien la hace. */
  confirmarCita(proyectoId: Identifier, confirmada: boolean): Promise<void>;
  /** 39: quién agendó la consultoría y quién la hace (vacío sin la 39). */
  personasConsultoria(proyectoId: Identifier): Promise<PersonaConsultoria[]>;
  /** 39: el importe por el que se ha aceptado (antes de pasar a «Aceptada»). Solo administradores. */
  ponerImporteAceptado(proyectoId: Identifier, importe: number): Promise<void>;
  /** 43: las colaboraciones de la web (Jan y Robert); null si la base aún no las tiene. */
  colaboraciones(): Promise<Colaboracion[] | null>;
  marcarColaboracion(id: Identifier, estado: EstadoColaboracion): Promise<void>;
  /** 43: «Pasar a ficha»: devuelve el id del negocio. */
  colaboracionAFicha(id: Identifier): Promise<Identifier | null>;
  /** 42: el enlace de Meet de la consultoría (quien ve la ficha); null si no hay. */
  meetConsultoria(proyectoId: Identifier): Promise<string | null>;
  /** 42: marca (o quita) «Sin respuesta»: quien hace la consultoría o Jan y Robert. */
  marcarSinRespuesta(
    proyectoId: Identifier,
    sinRespuesta: boolean,
  ): Promise<void>;
  /** 42: «El cliente pide otra hora»: aviso a quien la agendó y a Jan y Robert. */
  pedirOtraHora(proyectoId: Identifier, texto: string): Promise<void>;
  /** 42: nota del consultor en una consultoría suya (también tras «Se hizo»). */
  anotarConsultor(
    proyectoId: Identifier,
    texto: string,
    canal: string,
  ): Promise<void>;
  /** 36: lo que ve un setter de sus ventas y comisiones. */
  misComisiones(): Promise<MiComision[]>;
  /** 36: Jan o Robert marcan (o desmarcan) la comisión de un cobro como pagada. */
  marcarComisionPagada(cobroId: Identifier, pagada: boolean): Promise<void>;
  /** 40: Jan o Robert marcan (o desmarcan) como pagada la comisión de una persona por un cobro. */
  marcarComision(comisionId: Identifier, pagada: boolean): Promise<void>;
  /** 41: horas en las que se puede agendar una consultoría (al menos una persona libre; sin nombres). */
  huecosConsultoria(desde?: FechaISO, dias?: number): Promise<string[]>;
  /**
   * 41: agenda la consultoría en un hueco (la base elige quién la hace y registra «Quiere reunión» con
   * los datos de la hoja) o, si ya está agendada, le cambia la hora. Devuelve quién la hace.
   */
  agendarConsultoria(params: {
    negocio: Negocio;
    canal: Canal;
    inicio: string;
    datos?: DatosResultado;
    ajustes?: Pick<AjustesNevora, "maxIntentos">;
  }): Promise<Identifier>;
  /** 41: el horario de consultorías y el calendario de Google de una persona. */
  agendaDe(
    saleId: Identifier,
  ): Promise<{ horario: unknown; calendario_google: string | null } | null>;
  guardarAgenda(
    saleId: Identifier,
    horario: unknown,
    calendario: string | null,
  ): Promise<void>;
  /** 41: quién tiene horario y la última pasada de Google (Jan y Robert). */
  agendaEstado(): Promise<EstadoAgenda>;
  /** 41: pide a la función `agenda` que sincronice ya con Google (Jan y Robert). */
  sincronizarAgenda(): Promise<Record<string, unknown>>;
  /** 40: consultorías, hechas, «No se presentó», cierres y comisión de quien hace consultorías. */
  metricasConsultores(
    desde: string,
    hasta: string,
  ): Promise<MetricasConsultor[]>;
  /** 36: correos de Prospect a la ficha (vacío sin la función de Prospect o sin la 36). */
  correosDeNegocio(negocioId: Identifier): Promise<CorreoDeNegocio[]>;
  /** 36: entradas de la lista de no contactar de una ficha (vacío si no está o no se ve). */
  noContactarDe(negocioId: Identifier): Promise<EntradaNoContactar[]>;
};

/**
 * Altas en las que el setter se quedó un descubierto de Prospect (S10b). Van aparte y no dentro
 * del registro: si el registro creado llevara un campo de más, la recarga de la ficha recién
 * abierta lo vería distinto y rehará el formulario, perdiendo lo que el usuario ya hubiera
 * tocado (08/10/2026, la E2E A17 contra el proyecto de pruebas).
 */
const altasReclamadas = new Set<string>();

/** La base aún no tiene nevora_posibles_duplicados (llega con la migración 28). */
let sinFuncionDuplicados = false;

/** ¿El alta de este negocio fue quedarse un descubierto? Se consulta una vez. */
export const fueReclamada = (id: Identifier): boolean =>
  altasReclamadas.delete(String(id));

/** Columnas del negocio que la app escribe (tabla negocios). */
const COLUMNAS_NEGOCIO = [
  "nombre",
  "sector",
  "direccion",
  "telefono",
  "web",
  "persona_contacto",
  "cargo_contacto",
  "es_decisor",
  "email",
  "origen",
  "asignado_a",
  "no_llamar",
  "place_id",
] as const;

/** Columnas del proyecto que la app escribe (tabla proyectos; `proyecto_nombre` → `nombre`). */
const COLUMNAS_PROYECTO = [
  "etapa",
  "orden",
  "siguiente_paso",
  "fecha_siguiente_paso",
  // 33: hora opcional del siguiente paso (campo del formulario de la ficha).
  "hora_siguiente_paso",
  "fecha_reunion",
  "fecha_presentacion",
  "fecha_vuelta",
  "valor_estimado",
  "importe_total",
  "proyecto_nombre",
  // El nivel de interés (28d) NO va aquí: se guarda aparte (cambiarInteres), para que un
  // formulario a medias no devuelva el nivel anterior al guardar.
] as const;

/** Lo que viene del proyecto en la vista (para enseñar otro proyecto del mismo negocio). */
const CAMPOS_PROYECTO_VISTA = [
  "proyecto_id",
  "proyecto_nombre",
  "embudo",
  "etapa",
  "orden",
  "intentos",
  "siguiente_paso",
  "fecha_siguiente_paso",
  "fecha_reunion",
  "fecha_presentacion",
  "fecha_vuelta",
  "etapa_antes_de_aparcar",
  "valor_estimado",
  "importe_total",
  "cobrado_at",
  "comercial_id",
  "proyecto_updated_at",
  "agendado_por",
] as const;

type Datos = Record<string, unknown>;

/**
 * Un solo término con comodines: ra-data-postgrest rompe el "or" al partir por espacios cuando
 * hay 3+ palabras (segundo término sin campo → 400 del servidor). Paréntesis, comas, puntos y
 * comillas romperían la sintaxis `or=(...)` de PostgREST: se convierten también en comodín.
 */
const patronBusqueda = (q: string) =>
  q
    .split(/[\s(),."'\\]+/)
    .filter(Boolean)
    .join("*");

/** La búsqueda libre como condición «o» de PostgREST. */
const orBusqueda = (patron: string) =>
  ["nombre", "persona_contacto", "telefono", "direccion", "email"]
    .map((c) => `${c}.ilike.*${patron}*`)
    .join(",");

/**
 * Lista de una vista con una o varias condiciones «o» de PostgREST (que se juntan con «y»), más
 * filtros simples (`columna` = valor o `columna@operador`), orden y página.
 */
const leerConCondicionesO = async (
  vista: string,
  params: {
    pagination?: { page: number; perPage: number };
    sort?: { field: string; order: string };
  },
  filtro: Datos,
  condicionesO: string[],
  /** 47: varias columnas de orden (si no, la del parámetro sort). */
  orden?: string[],
) => {
  const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
  const { field, order } = params.sort ?? { field: "id", order: "ASC" };
  let consulta = getSupabaseClient()
    .from(vista)
    .select("*", { count: "exact" });
  for (const [clave, valor] of Object.entries(filtro)) {
    const [columna, operador] = clave.split("@");
    consulta = operador
      ? consulta.filter(
          columna,
          operador,
          valor === null ? "null" : String(valor),
        )
      : consulta.eq(columna, valor as string);
  }
  for (const o of condicionesO) consulta = consulta.or(o);
  for (const columna of orden ?? [field])
    consulta = consulta.order(columna, {
      ascending: !orden ? order === "ASC" : true,
    });
  const { data, count, error } = await consulta.range(
    (page - 1) * perPage,
    page * perPage - 1,
  );
  if (error) throw mensajeDeError(error);
  return { data: data ?? [], total: count ?? 0 };
};

/** 47: orden de la vista negocios_para_llamar (espejo de dominio/llamadas.ts → ordenParaLlamar). */
const ORDEN_PARA_LLAMAR = [
  "llamada_grupo",
  "llamada_prioridad",
  "llamada_desde",
  "id",
];

/**
 * 47 (auditoría 31/10/2026): «a quién llamar primero» ordenado por la base, sin tope de fichas.
 * Mismos filtros que la lista normal (búsqueda libre y personas).
 */
const leerParaLlamar = async (
  params: Parameters<CrmDataProvider["getList"]>[1],
) => {
  const { q, personas, ...resto } = (params.filter ?? {}) as Datos;
  const patron = q ? patronBusqueda(String(q)) : "";
  const condicion = condicionPersonas(
    Array.isArray(personas) ? (personas as string[]) : [],
  );
  return leerConCondicionesO(
    "negocios_para_llamar",
    params,
    { ...resto, ...(condicion.tipo === "simple" ? condicion.filtro : {}) },
    [
      ...(condicion.tipo === "o" ? [condicion.expresion] : []),
      ...(patron ? [orBusqueda(patron)] : []),
    ],
    ORDEN_PARA_LLAMAR,
  );
};

/** Lista de negocios con la búsqueda libre y el filtro por persona (antes, el cuerpo de getList). */
const crearLeerNegocios =
  (base: CrmDataProvider) =>
  async (params: Parameters<CrmDataProvider["getList"]>[1]) => {
    // Búsqueda libre (`q`) en negocios: nombre, persona de contacto, teléfono, dirección y
    // email; y, para Jan y Robert, filtro por persona (`personas`, selección múltiple,
    // 13/10/2026).
    const { q, personas, ...resto } = (params.filter ?? {}) as Datos;
    const patron = q ? patronBusqueda(String(q)) : "";
    const condicion = condicionPersonas(
      Array.isArray(personas) ? (personas as string[]) : [],
    );
    const filter: Datos = {
      ...resto,
      ...(condicion.tipo === "simple" ? condicion.filtro : {}),
    };
    // Personas y «sin asignar» a la vez piden una condición «o» propia, que ra-data-postgrest
    // no sabe juntar con la de la búsqueda: esa consulta se hace aquí (PostgREST junta las
    // dos con «y»; comprobado contra el proyecto de pruebas el 13/10/2026).
    if (condicion.tipo === "o") {
      return leerConCondicionesO("negocios_estado", params, filter, [
        condicion.expresion,
        ...(patron ? [orBusqueda(patron)] : []),
      ]) as never;
    }
    return base.getList("negocios_estado", {
      ...params,
      filter: patron
        ? {
            ...filter,
            "@or": {
              "nombre@ilike": patron,
              "persona_contacto@ilike": patron,
              "telefono@ilike": patron,
              "direccion@ilike": patron,
              "email@ilike": patron,
            },
          }
        : filter,
    });
  };

/** Las columnas de `columnas` que cambian respecto a `anterior` (todas si no hay anterior). */
const cambios = (
  datos: Datos,
  columnas: readonly string[],
  anterior?: Datos,
): Datos =>
  Object.fromEntries(
    columnas
      .filter((c) => c in datos)
      .filter((c) => !anterior || datos[c] !== anterior[c])
      .map((c) => [c === "proyecto_nombre" ? "nombre" : c, datos[c]]),
  );

/**
 * Escribir en negocios o proyectos SIN pedir la fila de vuelta (38, 22/10/2026): la app ya no lee los
 * importes de las tablas (solo de las vistas), así que una escritura que devolviera la fila entera
 * fallaría. Después se relee por las vistas.
 */
const escribir = async (
  tabla: "negocios" | "proyectos",
  id: Identifier,
  datos: Datos,
) => {
  const { error, count } = await getSupabaseClient()
    .from(tabla)
    .update(datos, { count: "exact" })
    .eq("id", id);
  if (error) throw mensajeDeError(error);
  // Sin permiso, la base no da error: simplemente no cambia ninguna fila.
  if (count === 0)
    throw new Error("No se ha guardado: no puedes cambiar esta ficha");
};

const insertar = async (tabla: "proyectos", datos: Datos) => {
  const { error } = await getSupabaseClient().from(tabla).insert(datos);
  if (error) throw mensajeDeError(error);
};

const mensajeDeError = (e: unknown) => {
  if (e instanceof Error) return e;
  const m = (e as { message?: string })?.message;
  return new Error(m ?? "Error desconocido");
};

/**
 * Proveedor de datos de Nevora Sales: el de Atomic (ra-supabase + callbacks) más lo
 * propio. Desde S8 el recurso `negocios` de la app es un negocio con su proyecto en foco:
 * se LEE de la vista negocios_estado (o de proyectos_vista para otro proyecto del mismo
 * negocio) y al guardar se reparte entre las tablas negocios y proyectos. Las reglas que
 * mueven proyectos viven en la base (registrar_resultado, registrar_cobro, triggers); aquí
 * se validan antes con el dominio para avisar en castellano.
 */
export const getNevoraDataProvider = (): NevoraDataProvider => {
  const base = getAtomicDataProvider();
  const lectura = (resource: string) =>
    resource === "negocios" ? "negocios_estado" : resource;
  const leerNegocios = crearLeerNegocios(base);
  // 47: ¿la base ya tiene negocios_para_llamar? (se averigua la primera vez).
  let paraLlamarEnLaBase: boolean | undefined;

  const leerNegocio = async (
    id: Identifier,
    proyecto?: Identifier | null,
  ): Promise<Negocio> => {
    const { data } = await base.getOne<Negocio>("negocios_estado", { id });
    if (proyecto != null && String(proyecto) !== String(data.proyecto_id)) {
      const { data: otro } = await base.getOne<Negocio>("proyectos_vista", {
        id: proyecto,
      });
      if (String(otro.negocio_id) === String(id)) {
        const campos: Datos = Object.fromEntries(
          CAMPOS_PROYECTO_VISTA.map((c) => [c, otro[c]]),
        );
        // La marca «Interesado» (28c) es del proyecto; sin la 28c en la base no viene.
        if ("interesado_at" in otro) campos.interesado_at = otro.interesado_at;
        if ("interes" in otro) campos.interes = otro.interes;
        if ("hora_siguiente_paso" in otro)
          campos.hora_siguiente_paso = otro.hora_siguiente_paso;
        // 36: No show y cita confirmada (sin la 36 no vienen).
        if ("no_shows" in otro) campos.no_shows = otro.no_shows;
        if ("consultoria_confirmada_at" in otro)
          campos.consultoria_confirmada_at = otro.consultoria_confirmada_at;
        return { ...data, ...campos, id: data.id } as Negocio;
      }
    }
    return data;
  };

  const rpc = async <T>(funcion: string, params: Datos): Promise<T> => {
    const { data, error } = await getSupabaseClient().rpc(funcion, params);
    if (error) throw mensajeDeError(error);
    return data as T;
  };

  return {
    ...base,

    async getList(resource, params) {
      if (resource !== "negocios") return base.getList(resource, params);
      // 46 (auditoría 30/10/2026): «a quién llamar primero». Desde la 47 lo ordena la base
      // (negocios_para_llamar); sin la 47, se traen las fichas del filtro (hasta 1.000) y se
      // ordenan aquí.
      if (params.sort?.field === ORDEN_LLAMADA) {
        if (paraLlamarEnLaBase !== false) {
          try {
            const r = await leerParaLlamar(params);
            paraLlamarEnLaBase = true;
            return r as never;
          } catch (e) {
            if (!esAnaliticaNoDisponible(e)) throw e;
            paraLlamarEnLaBase = false;
          }
        }
        const todas = (await leerNegocios({
          ...params,
          pagination: { page: 1, perPage: 1000 },
          sort: { field: "created_at", order: "ASC" },
        })) as { data: Negocio[]; total?: number };
        const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
        const ordenadas = ordenParaLlamar(todas.data, new Date());
        return {
          data: ordenadas.slice((page - 1) * perPage, page * perPage),
          total: todas.total ?? ordenadas.length,
        } as never;
      }
      return leerNegocios(params) as never;
    },

    async getOne(resource, params) {
      if (resource === "negocios") {
        const data = await leerNegocio(
          params.id,
          (params.meta as { proyecto?: Identifier } | undefined)?.proyecto,
        );
        return { data } as never;
      }
      return base.getOne(resource, params);
    },

    async getMany(resource, params) {
      return base.getMany(lectura(resource), params);
    },

    // 38: borrar sin pedir la fila de vuelta (la app no lee los importes de la tabla; si los pidiera,
    // la base respondería «sin permiso» y la app lo tomaría como sesión caducada). Los mensajes de la
    // base (S7: «no llamar» sin señal; S8: cliente con cobros) llegan tal cual.
    async delete(resource, params) {
      if (resource !== "negocios" && resource !== "proyectos")
        return base.delete(resource, params);
      const { error } = await getSupabaseClient()
        .from(resource)
        .delete()
        .eq("id", params.id);
      if (error) throw mensajeDeError(error);
      return { data: params.previousData ?? { id: params.id } } as never;
    },

    async deleteMany(resource, params) {
      if (resource !== "negocios" && resource !== "proyectos")
        return base.deleteMany(resource, params);
      const { error } = await getSupabaseClient()
        .from(resource)
        .delete()
        .in("id", params.ids as (string | number)[]);
      if (error) throw mensajeDeError(error);
      return { data: params.ids } as never;
    },

    async getManyReference(resource, params) {
      return base.getManyReference(lectura(resource), params);
    },

    // Alta: el negocio (email normalizado, misma regla que el trigger de S2) por la puerta
    // única de la base (S10b, nevora_alta_negocio: el setter es el dueño y se queda un
    // descubierto de Prospect libre en vez de recibir «ya está en el CRM») y, si nace fuera
    // de «Lead nuevo» o con siguiente paso o valor, su proyecto (el alta lo crea solo).
    async create(resource, params) {
      if (resource !== "negocios") return base.create(resource, params);
      const datos = params.data as Datos;
      const negocio = cambios(datos, COLUMNAS_NEGOCIO);
      if ("email" in negocio) {
        negocio.email = normalizarEmail(negocio.email as string | null);
      }
      const alta = await rpc<{ id: Identifier; reclamado: boolean }>(
        "nevora_alta_negocio",
        { p_datos: negocio },
      );
      const creado = { id: alta.id };
      if (alta.reclamado) altasReclamadas.add(String(alta.id));
      const proyecto = cambios(datos, COLUMNAS_PROYECTO);
      if (proyecto.etapa === "nuevo") delete proyecto.etapa;
      for (const [k, v] of Object.entries(proyecto)) {
        if (v === null || v === undefined || v === "") delete proyecto[k];
      }
      const leido = await leerNegocio(creado.id);
      if (Object.keys(proyecto).length) {
        await escribir("proyectos", leido.proyecto_id, proyecto);
        return { data: await leerNegocio(creado.id) } as never;
      }
      return { data: leido } as never;
    },

    // Ficha: cada columna a su tabla, solo lo que ha cambiado.
    async update(resource, params) {
      if (resource !== "negocios") return base.update(resource, params);
      const datos = params.data as Datos;
      const anterior = params.previousData as Negocio | undefined;
      const negocio = cambios(datos, COLUMNAS_NEGOCIO, anterior);
      if ("email" in negocio) {
        negocio.email = normalizarEmail(negocio.email as string | null);
      }
      const proyecto = cambios(datos, COLUMNAS_PROYECTO, anterior);
      if (anterior && proyecto.etapa) {
        validarCambioEtapa(anterior, proyecto.etapa as Etapa);
      }
      if (Object.keys(negocio).length) {
        await escribir("negocios", params.id, negocio);
      }
      const proyectoId = (datos.proyecto_id ?? anterior?.proyecto_id) as
        | Identifier
        | undefined;
      if (Object.keys(proyecto).length && proyectoId != null) {
        await escribir("proyectos", proyectoId, proyecto);
      }
      return {
        data: await leerNegocio(params.id, proyectoId),
      } as never;
    },

    async registrarResultado({ negocio, canal, resultado, datos, ajustes }) {
      // Valida con la misma regla que la base para avisar en castellano; guarda la base.
      const efecto = aplicarResultado(
        negocio,
        canal,
        resultado,
        datos,
        ajustes,
      );
      await rpc("registrar_resultado", {
        p_proyecto_id: negocio.proyecto_id,
        p_canal: canal,
        p_resultado: resultado,
        p_datos: datosParaRpc({ ...datos, fecha: efecto.nota.fecha }),
      });
      return { negocio: { ...negocio, ...efecto.cambios }, efecto };
    },

    async resumenLlamadas(desde, hasta) {
      const filas = await rpc<FilaResumen[]>("nevora_resumen_llamadas", {
        p_desde: desde.toISOString(),
        p_hasta: hasta.toISOString(),
      });
      return filas ?? [];
    },

    async resumenDisponible() {
      // Sin ninguna petición que pueda fallar (P1 no admite errores con una base sin la 28d): la
      // 28d trae a la vez el resumen y la columna `interes` de proyectos_vista.
      const { data, error } = await getSupabaseClient()
        .from("proyectos_vista")
        .select("*")
        .limit(1);
      if (error || !data?.length) return false;
      return "interes" in data[0];
    },

    async cambiarInteres(proyectoId, interes) {
      await escribir("proyectos", proyectoId, { interes });
    },

    async moverProyecto(proyecto, destino, extras = {}) {
      validarCambioEtapa(proyecto, destino);
      await escribir("proyectos", proyecto.proyecto_id, {
        etapa: destino,
        ...extras,
      });
    },

    async ordenarProyecto(proyectoId, orden) {
      await escribir("proyectos", proyectoId, { orden });
    },

    async registrarCobro({
      proyectoId,
      importe,
      fecha,
      importeTotal,
      nota,
      concepto,
    }) {
      await rpc("registrar_cobro", {
        p_proyecto_id: proyectoId,
        p_importe: importe,
        p_fecha: fecha,
        p_importe_total: importeTotal ?? null,
        p_nota: nota ?? null,
        p_concepto: concepto ?? "proyecto",
      });
    },

    async configurarComercial(saleId, datos) {
      await rpc("nevora_configurar_comercial", {
        p_sale_id: saleId,
        p_rol_comercial: datos.rol_comercial,
        p_recibe_leads: datos.recibe_leads,
        p_porcentaje_reparto: datos.porcentaje_reparto,
        p_tope_pendientes: datos.tope_pendientes,
        p_comision_pct: datos.comision_pct,
        p_team_lead_id: datos.team_lead_id ?? null,
      });
    },

    async panel(desde, hasta, personas, origen) {
      return rpc<DatosPanel>("nevora_panel", {
        p_desde: desde,
        p_hasta: hasta,
        p_personas: personas?.length ? personas : null,
        p_origen: origen || null,
      });
    },

    async panelAnalisis(desde, hasta, personas, origen) {
      return rpc<DatosAnalisis>("nevora_panel_analisis", {
        p_desde: desde,
        p_hasta: hasta,
        p_personas: personas?.length ? personas : null,
        p_origen: origen || null,
      });
    },

    async informeMensual(meses, personas, origen) {
      const r = await rpc<MesInforme[] | null>("nevora_informe_mensual", {
        p_meses: meses,
        p_personas: personas?.length ? personas : null,
        p_origen: origen || null,
      });
      return r ?? [];
    },

    async moverConMotivo(proyecto, destino, motivo, fechaVuelta) {
      validarCambioEtapa(proyecto, destino);
      try {
        await rpc("nevora_mover_con_motivo", {
          p_proyecto_id: proyecto.proyecto_id,
          p_destino: destino,
          p_motivo: motivo,
          p_fecha_vuelta: fechaVuelta || null,
        });
      } catch (e) {
        if (!esAnaliticaNoDisponible(e)) throw e;
        await escribir("proyectos", proyecto.proyecto_id, {
          etapa: destino,
          ...(destino === "largo_plazo" ? { fecha_vuelta: fechaVuelta } : {}),
        });
      }
    },

    async panelAlertas(personas) {
      const r = await rpc<AlertaPanel[] | null>("nevora_panel_alertas", {
        p_personas: personas?.length ? personas : null,
      });
      return r ?? [];
    },

    async asignar(ids, a) {
      return rpc<number>("nevora_asignar", { p_ids: ids, p_a: a });
    },

    async consultoriasSemana(fecha) {
      return rpc<number>("nevora_consultorias_semana", { p_fecha: fecha });
    },

    async metricasEquipo(desde, hasta) {
      return rpc<MetricasSetter[]>("nevora_metricas_equipo", {
        p_desde: desde,
        p_hasta: hasta,
      });
    },

    async inscripcionViva(negocioId) {
      return rpc<boolean>("nevora_inscripcion_viva", {
        p_negocio_id: negocioId,
      });
    },

    async sacarDeCampana(negocioId) {
      return rpc<number>("nevora_sacar_de_campana", {
        p_negocio_id: negocioId,
      });
    },

    async etapasNuevasEnLaBase() {
      // Sin la 34 la consulta devuelve una lista vacía (no un error): no ensucia la consola.
      const { data, error } = await getSupabaseClient()
        .from("etapas")
        .select("clave")
        .in("clave", ETAPAS_NUEVAS);
      if (error) return [];
      return (data ?? []).map((r: { clave: string }) => r.clave as Etapa);
    },

    async sacarDeCampanaDisponible() {
      // Sin negocio la función responde con un error suyo; sin la 32, PostgREST no la encuentra.
      const { error } = await getSupabaseClient().rpc(
        "nevora_sacar_de_campana",
        { p_negocio_id: null },
      );
      return error?.code !== "PGRST202";
    },

    async marcarAvisosLeidos(ids) {
      if (!ids.length) return;
      const { error } = await getSupabaseClient()
        .from("avisos")
        .update({ leido_at: new Date().toISOString() })
        .in("id", ids as (string | number)[]);
      if (error) throw mensajeDeError(error);
    },

    async capturasProspect(negocioId) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: { tipo: string; url: string | null }[];
      }>("capturas", { body: { negocio_id: negocioId } });
      if (error || !data) throw mensajeDeError(error ?? "Sin capturas");
      return data.data;
    },

    async posiblesDuplicados(nombre) {
      if (sinFuncionDuplicados) return null;
      const { data, error } = await getSupabaseClient().rpc(
        "nevora_posibles_duplicados",
        { p_nombre: nombre },
      );
      if (error) {
        // Hasta la migración 28 la función no existe: se deja de preguntar.
        if (error.code === "PGRST202") {
          sinFuncionDuplicados = true;
          return null;
        }
        throw mensajeDeError(error);
      }
      return data as PosiblesDuplicados;
    },

    async nuevoProyecto(negocioId, nombre) {
      try {
        await insertar("proyectos", {
          negocio_id: negocioId,
          etapa: "preparando_propuesta",
          nombre: nombre?.trim() || null,
        });
      } catch (e) {
        if (/proyectos_un_ventas_por_negocio/.test(mensajeDeError(e).message)) {
          throw new ErrorEtapa("nevora.etapas_errores.un_ventas");
        }
        throw e;
      }
    },

    async revisarLargoPlazo() {
      return rpc<number>("nevora_revisar_largo_plazo", {});
    },

    async borrarUsuario(saleId) {
      const { error } = await getSupabaseClient().functions.invoke("users", {
        method: "DELETE",
        body: { sales_id: saleId },
      });
      if (error) throw await detalleErrorFuncion(error);
    },

    async subirFotoUsuario(saleId, foto) {
      const ruta = `${saleId}/foto-${Date.now()}.webp`;
      const { error } = await getSupabaseClient()
        .storage.from("avatares")
        .upload(ruta, foto, { contentType: "image/webp", upsert: false });
      if (error) throw error;
      return ruta;
    },

    async cambiarFotoUsuario(saleId, ruta) {
      const { error } = await getSupabaseClient().functions.invoke("users", {
        method: "PATCH",
        body: { sales_id: saleId, foto: ruta },
      });
      if (error) throw await detalleErrorFuncion(error);
    },

    async urlFotoUsuario(ruta) {
      const { data, error } = await getSupabaseClient()
        .storage.from("avatares")
        .createSignedUrl(ruta, 60 * 60);
      return error ? null : (data?.signedUrl ?? null);
    },

    async borrarFicheroFoto(ruta) {
      await getSupabaseClient().storage.from("avatares").remove([ruta]);
    },

    async pushDisponible() {
      const { error } = await getSupabaseClient()
        .from("push_suscripciones")
        .select("id", { head: true, count: "exact" })
        .limit(1);
      return !error;
    },

    async misMovilesPush() {
      const { data, error } = await getSupabaseClient()
        .from("push_suscripciones")
        .select(
          "id, endpoint, dispositivo, created_at, ultimo_envio_at, ultimo_error",
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as MovilPush[];
    },

    async guardarMovilPush(movil) {
      await rpc("nevora_push_guardar", {
        p_endpoint: movil.endpoint,
        p_p256dh: movil.p256dh,
        p_auth: movil.auth,
        p_dispositivo: movil.dispositivo,
      });
    },

    async quitarMovilPush({ endpoint, id }) {
      if (endpoint) {
        await rpc("nevora_push_quitar", { p_endpoint: endpoint });
        return;
      }
      const { error } = await getSupabaseClient()
        .from("push_suscripciones")
        .delete()
        .eq("id", id!);
      if (error) throw error;
    },

    async clavePublicaPush() {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        clavePublica: string;
      }>("push", { method: "GET" });
      if (error || !data?.clavePublica) {
        throw await detalleErrorFuncion(error ?? new Error("Sin clave"));
      }
      return data.clavePublica;
    },

    async pruebaPush() {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        enviadas: number;
        moviles: number;
      }>("push", { method: "POST", body: { accion: "prueba" } });
      if (error || !data) throw await detalleErrorFuncion(error);
      return data;
    },

    async ultimosAccesos() {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: { user_id: string; last_sign_in_at: string | null }[];
      }>("users", { method: "GET" });
      if (error || !data) throw error ?? new Error("Sin datos de acceso");
      return Object.fromEntries(
        data.data.map((a) => [a.user_id, a.last_sign_in_at]),
      );
    },

    async consultoresPosibles() {
      return (
        (await rpc<ConsultorPosible[]>("nevora_consultores_posibles", {})) ?? []
      );
    },

    async consultoresDe(proyectoId) {
      const { data, error } = await getSupabaseClient()
        .from("proyecto_consultores")
        .select("sale_id, orden")
        .eq("proyecto_id", proyectoId)
        .order("orden", { ascending: true });
      if (error) return [];
      return (data ?? []).map((r: { sale_id: Identifier }) => r.sale_id);
    },

    async ponerConsultores(proyectoId, personas) {
      await rpc("nevora_poner_consultores", {
        p_proyecto_id: proyectoId,
        p_personas: personas,
      });
    },

    async confirmarCita(proyectoId, confirmada) {
      await escribir("proyectos", proyectoId, {
        consultoria_confirmada_at: confirmada ? new Date().toISOString() : null,
      });
    },

    async personasConsultoria(proyectoId) {
      try {
        return (
          (await rpc<PersonaConsultoria[]>("nevora_personas_consultoria", {
            p_proyecto_id: proyectoId,
          })) ?? []
        );
      } catch {
        return [];
      }
    },

    async ponerImporteAceptado(proyectoId, importe) {
      await escribir("proyectos", proyectoId, { importe_total: importe });
    },

    async colaboraciones() {
      const { data, error } = await getSupabaseClient()
        .from("colaboraciones")
        .select("*")
        .order("recibida_at", { ascending: false })
        .limit(500);
      if (error) return null;
      return (data ?? []) as Colaboracion[];
    },

    async marcarColaboracion(id, estado) {
      const { error } = await getSupabaseClient()
        .from("colaboraciones")
        .update({ estado }, { count: "exact" })
        .eq("id", id);
      if (error) throw new Error(error.message);
    },

    async colaboracionAFicha(id) {
      return (
        (await rpc<Identifier | null>("nevora_colaboracion_a_ficha", {
          p_id: id,
        })) ?? null
      );
    },

    async meetConsultoria(proyectoId) {
      return (
        (await rpc<string | null>("nevora_meet_consultoria", {
          p_proyecto_id: proyectoId,
        })) ?? null
      );
    },

    async marcarSinRespuesta(proyectoId, sinRespuesta) {
      await escribir("proyectos", proyectoId, {
        consultoria_sin_respuesta_at: sinRespuesta
          ? new Date().toISOString()
          : null,
      });
    },

    async pedirOtraHora(proyectoId, texto) {
      await rpc("nevora_pedir_otra_hora", {
        p_proyecto_id: proyectoId,
        p_texto: texto,
      });
    },

    async anotarConsultor(proyectoId, texto, canal) {
      await rpc("nevora_anotar_consultor", {
        p_proyecto_id: proyectoId,
        p_texto: texto,
        p_canal: canal,
      });
    },

    async misComisiones() {
      return (await rpc<MiComision[]>("nevora_mis_comisiones", {})) ?? [];
    },

    async marcarComisionPagada(cobroId, pagada) {
      await rpc("nevora_marcar_comision_pagada", {
        p_cobro_id: cobroId,
        p_pagada: pagada,
      });
    },

    async marcarComision(comisionId, pagada) {
      await rpc("nevora_marcar_comision", {
        p_comision_id: comisionId,
        p_pagada: pagada,
      });
    },

    async huecosConsultoria(desde, dias) {
      const filas = await rpc<{ inicio: string }[] | string[]>(
        "nevora_huecos",
        {
          p_desde: desde ?? null,
          p_dias: dias ?? null,
        },
      );
      return (filas ?? []).map((f) => (typeof f === "string" ? f : f.inicio));
    },

    async agendarConsultoria({ negocio, canal, inicio, datos, ajustes }) {
      let pDatos: Datos = {};
      if (negocio.etapa !== "consultoria_agendada" && datos) {
        // Valida con la misma regla que la base para avisar en castellano; la base registra.
        const efecto = aplicarResultado(
          negocio,
          canal,
          "quiere_reunion",
          { ...datos, fechaReunion: inicio },
          ajustes ?? { maxIntentos: 3 },
        );
        pDatos = datosParaRpc({
          ...datos,
          fechaReunion: inicio,
          fecha: efecto.nota.fecha,
        });
      }
      return rpc<Identifier>("nevora_agendar_consultoria", {
        p_proyecto_id: negocio.proyecto_id,
        p_canal: canal,
        p_inicio: inicio,
        p_datos: pDatos,
      });
    },

    async agendaDe(saleId) {
      const { data, error } = await getSupabaseClient()
        .from("agenda_personas")
        .select("horario, calendario_google")
        .eq("sale_id", saleId)
        .maybeSingle();
      if (error) return null;
      return data;
    },

    async guardarAgenda(saleId, horario, calendario) {
      await rpc("nevora_guardar_agenda", {
        p_sale_id: saleId,
        p_horario: horario,
        p_calendario: calendario,
      });
    },

    async agendaEstado() {
      return rpc<EstadoAgenda>("nevora_agenda_estado", {});
    },

    async sincronizarAgenda() {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "agenda",
        {
          body: { accion: "sincronizar" },
        },
      );
      if (error) throw new Error(error.message);
      return (data ?? {}) as Record<string, unknown>;
    },

    async metricasConsultores(desde, hasta) {
      return (
        (await rpc<MetricasConsultor[]>("nevora_metricas_consultores", {
          p_desde: desde,
          p_hasta: hasta,
        })) ?? []
      );
    },

    async correosDeNegocio(negocioId) {
      // Sin la 36 PostgREST no encuentra la función: lista vacía, sin error a la vista.
      const { data, error } = await getSupabaseClient().rpc(
        "nevora_correos_de_negocio",
        { p_negocio_id: negocioId },
      );
      if (error) return [];
      return (data ?? []) as CorreoDeNegocio[];
    },

    async noContactarDe(negocioId) {
      const { data, error } = await getSupabaseClient()
        .from("no_contactar")
        .select("motivo, fecha, app_origen, canal")
        .eq("negocio_id", negocioId)
        .order("fecha", { ascending: true });
      if (error) return [];
      return (data ?? []) as EntradaNoContactar[];
    },

    async getAjustes() {
      const config = await base.getConfiguration();
      return leerAjustes(config);
    },

    async updateAjustes(ajustes) {
      const config = await base.getConfiguration();
      await base.updateConfiguration(
        escribirAjustes(
          config as unknown as Record<string, unknown>,
          ajustes,
        ) as never,
      );
      return ajustes;
    },
  };
};

/**
 * El cuerpo JSON de un error de una función (`{status, message, code, …}`), como Error con esos
 * campos; si no se puede leer, el error tal cual.
 */
const detalleErrorFuncion = async (error: unknown): Promise<unknown> => {
  const respuesta = (error as { context?: Response })?.context;
  try {
    const cuerpo = await respuesta?.json();
    if (cuerpo && typeof cuerpo === "object") {
      return Object.assign(
        new Error(String(cuerpo.message ?? "Error")),
        cuerpo,
      );
    }
  } catch {
    // sin cuerpo legible
  }
  return error;
};
